/**
 * MOBD-100 — Geocodificación, aislada a propósito.
 *
 * Todo lo que traduce "texto ↔ coordenada" vive AQUÍ y en ningún otro sitio. El proveedor actual es
 * Google (el panel ya carga su SDK y tiene la key en REACT_APP_GMAPS), pero el geocoding del producto
 * está migrando a OpenStreetMap en otra tarjeta: cuando toque, se reescriben estas dos funciones y el
 * resto del panel no se entera.
 *
 * Lo que NO se decide aquí: en qué zona cae un punto y cuánto cuesta. Eso lo resuelve siempre el
 * backend contra la tabla de zonas, que es la única fuente (MPDD-54: "reutilizar, no duplicar").
 */

export type GeoPlace = {
    address: string
    lat: number
    lng: number
    /** Población y código postal del punto, para seguir guardándolos como hasta ahora en el rent. */
    city: string
    postalCode: string
}

// Sesgo de resultados a España: sin esto, "Ourense" puede devolver calles homónimas de otro país.
const COUNTRY = 'ES'

/** Saca un componente de la dirección devuelta por el proveedor (locality, postal_code…). */
const component = (result: any, type: string): string => {
    const parts: any[] = result?.address_components || []
    const found = parts.find((part) => (part.types || []).indexOf(type) >= 0)
    return found ? found.long_name || found.short_name || '' : ''
}

const geocoder = (): any | null => {
    const maps = (window as any).google?.maps
    if (!maps || !maps.Geocoder) {
        return null
    }
    return new maps.Geocoder()
}

const firstPlace = (results: any[]): GeoPlace | null => {
    if (!results || !results.length) {
        return null
    }
    const best = results[0]
    const location = best.geometry?.location
    if (!location) {
        return null
    }
    return {
        address: best.formatted_address || '',
        // lat()/lng() son funciones en el SDK; en algunos mocks llegan como número.
        lat: typeof location.lat === 'function' ? location.lat() : location.lat,
        lng: typeof location.lng === 'function' ? location.lng() : location.lng,
        // "locality" es la población; en núcleos pequeños Google no la da y cae al municipio.
        city:
            component(best, 'locality') ||
            component(best, 'administrative_area_level_2') ||
            '',
        postalCode: component(best, 'postal_code'),
    }
}

/** Dirección escrita → punto en el mapa. null si no se encuentra. */
export const searchAddress = (query: string): Promise<GeoPlace | null> =>
    new Promise((resolve) => {
        const service = geocoder()
        if (!service || !query || !query.trim()) {
            resolve(null)
            return
        }
        service.geocode(
            { address: query, componentRestrictions: { country: COUNTRY } },
            (results: any[], status: string) => {
                resolve(status === 'OK' ? firstPlace(results) : null)
            }
        )
    })

/**
 * Punto en el mapa → dirección legible. Si el proveedor no sabe resolverla se devuelven las
 * coordenadas formateadas: el rent se puede pedir igual, y logística ve al menos dónde ir.
 */
export const placeOfPoint = (lat: number, lng: number): Promise<GeoPlace> =>
    new Promise((resolve) => {
        const fallback: GeoPlace = {
            address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
            lat,
            lng,
            city: '',
            postalCode: '',
        }
        const service = geocoder()
        if (!service) {
            resolve(fallback)
            return
        }
        service.geocode({ location: { lat, lng } }, (results: any[], status: string) => {
            const place = status === 'OK' ? firstPlace(results) : null
            if (!place || !place.address) {
                resolve(fallback)
                return
            }
            // Se conservan lat/lng del pin, no las que devuelva el proveedor: el usuario marcó ESE
            // punto y es el que se validó contra la zona.
            resolve({ ...place, lat, lng })
        })
    })
